import type { ReactNode } from 'react';
import { useDevice } from 'app/hooks/compactTouchUI/DeviceContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material';
import { RecoilRoot } from 'recoil';
import { useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';

import CookieBanner from 'app/pages/Shared/CookieBanner';
import ModalContainer from 'react-modal-promise';
import { GlobalProvider } from 'app/Context/GlobalContext';
import { FCMListener } from 'app/services/FCMListener';
import NativeShellCommands from 'app/components/common/NativeShellCommands';
import LoadingMessageOverlay from 'app/components/common/LoadingMessageOverlay';
import {
  getUserEmail,
  setDataConsentScreenShown,
  setHasOfficeWorkAllocation,
  setProfiles,
  setUser,
} from 'app/utils/localStorageHandler/userProfile';
import { setPartnerData } from 'app/utils/localStorageHandler/partnerData';
import { initializeActiveBrand } from 'app/utils/localStorageHandler/brandData';
import { initializeCurrentRole } from 'app/utils/localStorageHandler/currentRole';
import { UserService } from 'app/services/user';
import { datadogIdentifyFromProfiles } from 'vendor/datadog';
import { usePostHog } from '@posthog/react';
import {
  PROTECTED_ROUTES_CONFIG,
  type RouteConfigEntry,
} from './routes/ProtectedRouteConfig';
import { getFirebaseApp, getFirebaseAuth } from 'app/utils/firebase';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { DocumentUploadBannerProvider } from 'app/Context/DocumentUploadBannerContext';
import { useDocumentUploadBanner } from 'app/hooks/useDocumentUploadBanner';
import { registerAppWorker } from 'app/platform/release/release';
import { AskMaxHost } from 'app/features/askMax/AskMaxHost';

const theme = createTheme({
  palette: { primary: { main: '#2F78EB' } },
});

// Stock default is 3 attempts; queries that want retries set their own.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

/**
 * Provider stack for protected (authenticated) routes: Recoil, Global,
 * Theme, Config, FCM, etc. Renders children (the matched route element).
 * Firebase is initialized here only when a protected route is active.
 */
export function ProtectedShell({ children }: { children: ReactNode }) {
  const { isMobileUI } = useDevice();

  const firebaseApp = getFirebaseApp();
  const documentUploadBanner = useDocumentUploadBanner();
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const posthog = usePostHog();
  // Undefined until the profile answers. Seeding from persisted consent would let register() write
  // ahead of a server-side revocation, and register persists the property client-side.
  const [trackingConsent, setTrackingConsent] = useState<boolean>();
  // Super-property rather than a one-off event, so every existing funnel can be split by device
  // without re-instrumenting each page. Adoption data is what decides the deferred mobile scope.
  useEffect(() => {
    if (trackingConsent !== true) return;
    posthog?.register({ deviceClass: isMobileUI ? 'mobile' : 'desktop' });
  }, [posthog, isMobileUI, trackingConsent]);
  const userID =
    typeof window !== 'undefined' ? localStorage.getItem('userId') || '' : '';
  // Banner is hidden by default; we only show it when BE explicitly returns false.
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(false);

  useEffect(() => {
    initializeCurrentRole();

    const auth = getFirebaseAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      if (!user) {
        queryClient.clear();
      }
    });

    const currentRoute = window.location.pathname;
    const route = PROTECTED_ROUTES_CONFIG.find((r) =>
      matchPath(r.path, currentRoute)
    ) as RouteConfigEntry | undefined;
    if (!route?.skipProfileFetchPath) {
      const userSvc = new UserService();
      userSvc.getUserProfile().then(
        (response) => {
          setProfiles(response?.data?.profiles);
          setPartnerData(response?.data?.partner || {});
          initializeActiveBrand(response?.data?.brands || []);
          setUser(response?.data?.userId);
          datadogIdentifyFromProfiles(
            response?.data?.userId,
            response?.data?.profiles ?? []
          );
          const dataTrackingScreenShown =
            response?.data?.dataTrackingScreenShown === true;

          setDataConsentScreenShown(dataTrackingScreenShown);
          setTrackingConsent(dataTrackingScreenShown);
          setShowCookieBanner(dataTrackingScreenShown === false);
          setHasOfficeWorkAllocation(
            response?.data?.hasOfficeWorkAllocation || false
          );
          if (dataTrackingScreenShown) {
            const currentUserID = response?.data?.userId || userID;
            const currentPartnerID = response?.data?.partner?._id;

            if (currentUserID && currentPartnerID) {
              posthog?.identify(currentUserID, {
                partner: currentPartnerID,
                email: getUserEmail(),
                partnerName: response?.data?.partner?.companyName,
              });
            }
          }
        },
        (error) => {
          console.error('Error fetching user profile: ', error);
        }
      );
    }

    return () => unsubscribeAuth();
  }, []);

  // Keeps this build's chunks available to this tab after the next deploy removes them.
  useEffect(() => {
    void registerAppWorker();
  }, []);

  return (
    <RecoilRoot>
      <GlobalProvider>
        <QueryClientProvider client={queryClient}>
          <FCMListener firebaseApp={firebaseApp} />
          <NativeShellCommands />
          <LoadingMessageOverlay />
          <ThemeProvider theme={theme}>
            {/* antd's ConfigProvider and App are mounted once in App.tsx, above
                both shells, so public routes get the same theme. */}
            <DocumentUploadBannerProvider bannerState={documentUploadBanner}>
              {children}
              <AskMaxHost />
            </DocumentUploadBannerProvider>
            <ModalContainer />
            {showCookieBanner && firebaseUser && (
              <CookieBanner privacyPolicyUrl="/privacy-policy" />
            )}
          </ThemeProvider>
        </QueryClientProvider>
      </GlobalProvider>
    </RecoilRoot>
  );
}
